import { AnyAction, Dispatch, Middleware, MiddlewareAPI } from 'redux'

interface AxiosRequestConfig {
  method?: string
  url?: string
  data?: unknown
  params?: Record<string, unknown>
  cancelToken?: unknown
  [key: string]: unknown
}

interface AxiosResponse<T = unknown> {
  data: T
  status: number
  statusText: string
  headers: unknown
  config: unknown
}

interface AxiosError {
  response?: {
    data: { message?: string }
    status: number
    statusText: string
  }
  message?: string
}

interface HttpClient {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: (config: any) => Promise<AxiosResponse>
}

export interface AutoThunkMiddlewareOptions {
  httpClient: HttpClient
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errorHandler?: (error: any, dispatch: any, action: any) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  track?: (...args: any[]) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  log?: (...args: any[]) => void
}

interface AutoThunkAction {
  type?: string
  action?:
    | string
    | { type: string; data?: unknown; [key: string]: unknown }
    | Array<string | { type: string; data?: unknown; [key: string]: unknown }>
  request?: [string, string, unknown?] | AxiosRequestConfig
  log?: string
  track?: unknown
  bodyType?: 'formData'
  errorHandler?: (error: AxiosError, dispatch: Dispatch<AnyAction>, action: unknown) => void
}

type ThunkFunction = (dispatch: Dispatch<AnyAction>, getState: () => unknown, extraArgument?: unknown) => unknown

const getErrorText = (statusCode: string): string | undefined => {
  const defaultErrorMessages: Record<string, string> = {
    '400': 'The request contained invalid data. Please double check the information provided and try again.',
    '401': 'Authorization failed for the given request. Please make sure you are logged in.',
    '404': 'Nothing found for the requested service',
    '413': 'The request your are trying to send is too large.',
    '500': 'The server is having issues. Please try again later.',
    '503': 'An error occurred in making the request. Please try again.',
    '504': 'The request has timed out. Please try again.',
  }
  return defaultErrorMessages[statusCode]
}

const defaultErrorHandler = (error: AxiosError): void => {
  if (!error || !error.response) {
    const unknownError = 'An unknown error has occurred'
    throw unknownError
  }
  const errorText = error.response.data.message || getErrorText(String(error.response.status))
  throw errorText
}

const createThunk = (
  config: Required<AutoThunkMiddlewareOptions>,
  data: unknown[] | AutoThunkAction | ThunkFunction,
): ThunkFunction | AnyAction => {
  if (typeof data === 'function') {
    return data as ThunkFunction
  }
  if (Array.isArray(data)) {
    data = { request: data as [string, string, unknown?] }
  }
  const thunkData = data as AutoThunkAction
  if (thunkData.type !== undefined && thunkData.type !== null) {
    return thunkData as AnyAction
  }

  let requestParams: AxiosRequestConfig = thunkData.request as AxiosRequestConfig
  if (Array.isArray(thunkData.request)) {
    requestParams = {
      method: thunkData.request[0],
      url: thunkData.request[1],
      data: thunkData.request[2],
    }
  }
  if (requestParams.data && thunkData.bodyType === 'formData') {
    const formData = new FormData()
    Object.entries(requestParams.data as Record<string, unknown>).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (typeof value === 'object') {
          value = JSON.stringify(value)
        }
        formData.append(key, value as string | Blob)
      }
    })
    requestParams.data = formData
  }

  return async (dispatch: Dispatch<AnyAction>) => {
    try {
      const res = await config.httpClient.request(requestParams)
      if (thunkData.log) {
        config.log(thunkData.log, res.data)
      }
      if (thunkData.track) {
        config.track(thunkData.track)
      }
      if (!thunkData.action) return res.data
      const actionsArray = Array.isArray(thunkData.action) ? thunkData.action : [thunkData.action]
      actionsArray.forEach((action) => {
        let dispatchAction: AnyAction
        if (typeof action === 'string') {
          dispatchAction = { type: action, data: res.data }
        } else if (typeof action === 'object' && (action.data === undefined || action.data === null)) {
          dispatchAction = { ...action, data: res.data }
        } else {
          dispatchAction = action as AnyAction
        }
        dispatch(dispatchAction)
      })
      return res.data
    } catch (error) {
      const axiosError = error as AxiosError
      if (axiosError.response) {
        if (thunkData.log) {
          config.log(thunkData.log, axiosError.response)
        }
      }
      const errorHandler = thunkData.errorHandler ? thunkData.errorHandler : config.errorHandler
      errorHandler(axiosError, dispatch, thunkData.action)
      throw error
    }
  }
}

export default function autoThunkMiddleware<ExtraArgument = undefined>(
  options: AutoThunkMiddlewareOptions,
  extraArgument?: ExtraArgument,
): Middleware<Record<string, never>, unknown, Dispatch<AnyAction>> {
  if (typeof options !== 'object' || typeof options.httpClient?.request !== 'function') {
    console.error('You must provide an http client to be able to make xhr requests. Supported libs are: axios.js')
    return (() => (next: Dispatch<AnyAction>) => (action: AnyAction) => next(action)) as Middleware
  }

  const config: Required<AutoThunkMiddlewareOptions> = {
    httpClient: options.httpClient,
    errorHandler: options.errorHandler || defaultErrorHandler,
    track: options.track || (() => {}),
    log: options.log || (() => {}),
  }

  return ({ dispatch, getState }: MiddlewareAPI) =>
    (next: Dispatch<AnyAction>) =>
    (action: unknown) => {
      if (!action) return
      const thunk = createThunk(config, action as unknown[] | AutoThunkAction | ThunkFunction)
      if (typeof thunk === 'function') {
        return thunk(dispatch, getState, extraArgument)
      }
      return next(thunk)
    }
}
